/* oxlint-disable typescript/no-deprecated */
import clsx from 'clsx';
import { forwardRef, useRef, type HTMLAttributes } from 'react';

import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import * as styles from './Main.sty.js';
import { usePageContext, useRegisterPagePanel } from './page-context.js';

/**
 * @public
 * @deprecated Deprecated along with the `Page` component. Please use `PageLayout.Content` instead.
 */
export interface MainProps
  extends
    WithChildren,
    StylingProps,
    DataTestId,
    Omit<HTMLAttributes<HTMLDivElement>, keyof StylingProps> {
  /**
   * The minimum width of the main panel in pixels.
   * @defaultValue 320
   */
  minWidth?: number;
}

/**
 * The `Main` component is used inside the `Page` component for displaying content in the corresponding panel.
 * It is the only compound that is mandatory for rendering the `Page`.
 * @public
 * @deprecated The `Page.Main` component is deprecated. Please use `PageLayout.Content` instead.
 */
export const Main = forwardRef<HTMLDivElement, MainProps>(
  (props, forwardedRef) => {
    const {
      children,
      minWidth = 320,
      className,
      style,
      ...remainingProps
    }: MainProps = props;

    const mainRef = useRef<HTMLDivElement>(null);
    const mergedRefs = useMergeRefs([mainRef, forwardedRef]);

    useRegisterPagePanel('main', {
      ref: mainRef,
      minWidth,
    });

    const { panels } = usePageContext();
    const hasSidebar = !!panels.sidebar;
    const hasDetails = !!panels.details && !panels.details.isDismissed;

    return (
      <main
        {...remainingProps}
        ref={mergedRefs}
        className={clsx(
          styles.main,
          hasSidebar && styles.withSidebar,
          hasDetails && styles.withDetails,
          className,
        )}
        style={{ ...style, minWidth }}
      >
        <div className={styles.content}>{children}</div>
      </main>
    );
  },
);

(Main as typeof Main & { displayName: string }).displayName = 'Page.Main';
